import dates from 'src/utils/dates'
import Networks from './networks'

const user = {
  defaults: {
    access: {
      active: false,
      expires: '2200-01-01T00:00:00',
      level: null,
      owner: null,
      permissions: {
        admin: false,
        alias: false,
        bulk: false,
        burtForecast: false,
        burtHub: false,
        customReports: false,
        demo: false,
        dev: false,
        discrepancy: false,
        enterprise: false,
        exports: false,
        gam: false,
        logs: false,
        pacing: false,
        programmatic: false,
        security: false,
        settings: true,
        users: false,
      },
      role: null,
      type: null,
    },
    domainName: null,
    email: null,
    emailVerified: null,
    enterpriseId: null,
    network: [],
    settings: {
      alias: {},
      bookmarks: [],
      dashboard: {},
      demo: {},
      discrepancy: {},
      filters: [],
      home: {},
      messages: {},
      networks: [],
      pacing: {},
      reports: [],
      themes: {
        value: 'system',
      },
      time: {},
    },
    tenantId: null,
  },

  validate: {
    network(network) {
      return Array.isArray(network) && !network.find((n) => {
        const hasValidOrganization = () => {
          const { organization } = n
          return typeof organization === 'object' && organization && organization.code && organization.id
        }
        const hasValidTeam = () => {
          const { team } = n
          return Array.isArray(team) && !team.find((t) => !t.id || !t.code)
        }
        return !hasValidOrganization() || !hasValidTeam()
      })
    },
    person(personUser) {
      const { isActive, isDeleted, personId } = personUser || {}
      const isExistingPersonRecord = () => !!personId || 'person/not-found'
      const hasValidIsActive = () => typeof isActive === 'boolean' || 'person/is-active'
      const hasValidIsDeleted = () => typeof isDeleted === 'boolean' || 'person/is-deleted'
      const hasFirstName = () => {
        const { personNameFirst } = personUser
        return (typeof personNameFirst === 'string' && personNameFirst.trim().length > 0) || 'person/name-first'
      }
      const hasLastName = () => {
        const { personNameLast } = personUser
        return (typeof personNameLast === 'string' && personNameLast.trim().length > 0) || 'person/name-last'
      }
      return [
        isExistingPersonRecord,
        hasValidIsActive,
        hasValidIsDeleted,
        hasFirstName,
        hasLastName,
      ]
    },
    user(personUser) {
      const { access, docId, network } = personUser || {}
      const { active, expires, level, role } = access || {}

      const isExistingUserRecord = () => !!docId || 'user/not-found'
      const hasValidAccess = () => typeof access === 'object' || 'user/access'
      const hasValidAccessActive = () => typeof active === 'boolean' || 'user/access-active'
      const hasValidAccessLevel = () => ['reader', 'writer', 'admin'].includes(level) || 'user/access-level'
      const hasValidAccessRole = () => ['external', 'internal'].includes(role) || 'user/access-role'

      const hasNetworkDefined = () => Array.isArray(network) || 'user/network-undefined'
      const hasNetworkConfigured = () => this.network(network) || 'user/network-config'

      const validateExpires = () => !expires || typeof expires === 'string'
      const hasValidExpires = () => validateExpires() || 'user/access-expires'

      return [
        isExistingUserRecord,
        hasValidAccess,
        hasValidAccessActive,
        hasValidAccessLevel,
        hasValidAccessRole,
        hasValidExpires,
        hasNetworkDefined,
        hasNetworkConfigured,
      ]
    },
  },
  validateRules(personUser, validators) {
    return validators.reduce((errors, validator) => {
      const validationResult = validator()
      if (validationResult !== true) errors.push(validationResult)
      return errors
    }, [])
  },
  validatePerson(personUser) {
    const validators = this.validate.person(personUser)
    return this.validateRules(personUser, validators)
  },
  validateUser(personUser) {
    const validators = this.validate.user(personUser)
    return this.validateRules(personUser, validators)
  },

  authorize: {
    person(record) {
      const { isActive, isDeleted, personId } = record || {}
      const hasPersonId = () => !!personId || 'person/auth-id'
      const isPersonActive = () => isActive || 'person/auth-inactive'
      const notPersonDeleted = () => !isDeleted || 'person/auth-deleted'

      return [
        hasPersonId,
        isPersonActive,
        notPersonDeleted,
      ]
    },
    user(record) {
      const { access, docId } = record || {}
      const { active, expires } = access || {}

      const validateEmail = (email) => typeof email === 'string' && !!email.length
      const hasDocId = () => validateEmail(docId) || 'user/auth-id'
      const isActive = () => active || 'user/auth-inactive'
      const notExpired = () => {
        const validates = () => !expires || dates.areDatesGte(expires, dates.now())
        return validates() || 'user/auth-expired'
      }

      return [
        hasDocId,
        isActive,
        notExpired,
      ]
    },
  },
  authorizePerson(personUser) {
    const validators = this.authorize.person(personUser)
    return this.validateRules(personUser, validators)
  },
  authorizeUser(personUser) {
    const validators = this.authorize.user(personUser)
    return this.validateRules(personUser, validators)
  },
  isAuthorizedPerson(personUser) {
    return this.authorizePerson(personUser).length === 0
  },
  isAuthorizedUser(personUser) {
    return this.authorizeUser(personUser).length === 0
  },

  /* eslint-disable-next-line complexity */
  normalizeSettings(assignedSettings) {
    const {
      alias: definedAlias = {},
      bookmarks: definedBookmarks = [],
      dashboard: definedDashboard = {},
      demo: definedDemo = {},
      discrepancy: definedDiscrepancy = {},
      filters: definedFilters = [],
      home: definedHome = {},
      messages: definedMessages = {},
      networks: definedNetworks = [],
      pacing: definedPacing = {},
      reports: definedReports = [],
      themes: definedThemes = {},
      time: definedTime = {},
    } = assignedSettings
    const hasValidAlias = typeof definedAlias === 'object'
    const hasValidBookmarks = Array.isArray(definedBookmarks) && definedBookmarks.length
    const hasValidDashboard = typeof definedDasbhboard === 'object'
    const hasValidDemo = typeof definedDemo === 'object'
    const hasValidDiscrepancy = typeof definedDiscrepancy === 'object'
    const hasValidHome = typeof definedHome === 'object'
    const hasValidMessages = typeof definedMessages === 'object'
    const hasValidNetworks = typeof definedNetworks === 'object'
    const hasValidPacing = typeof definedPacing === 'object'
    const hasValidReports = typeof definedReports === 'object'
    const hasValidThemes = typeof definedThemes === 'object'
    const hasValidTime = typeof definedTime === 'object'
    const hasValidUserFilters = Array.isArray(definedFilters) && definedFilters.length

    return {
      alias: (hasValidAlias && definedAlias) || {},
      bookmarks: (hasValidBookmarks && definedBookmarks) || [],
      dashboard: (hasValidDashboard && definedDashboard) || {},
      demo: (hasValidDemo && definedDemo) || { active: definedDemo || false },
      discrepancy: (hasValidDiscrepancy && definedDiscrepancy) || {},
      filters: (hasValidUserFilters && definedFilters) || [],
      home: (hasValidHome && definedHome) || {},
      messages: (hasValidMessages && definedMessages) || [],
      networks: (hasValidNetworks && definedNetworks) || [],
      pacing: (hasValidPacing && definedPacing) || {},
      reports: (hasValidReports && definedReports) || [],
      themes: (hasValidThemes && definedThemes) || {},
      time: (hasValidTime && definedTime) || {},
    }
  },

  makeDemoNetworks() {
    const organization = { code: 'DEMO2021', id: 10, networkCode: 8788 }
    const team = [{ teamId: '-1', teamCode: '-1' }]

    return [{ organization, team }]
  },
  inDemoMode(userRecord) {
    const { access = {}, settings = {} } = userRecord
    const { permissions, role } = access
    const isInternalUser = role === 'internal'
    const { demo: demoPermissions } = permissions || {}
    const { demo: demoSettings } = settings || {}
    const isDemoExternal = !isInternalUser && demoPermissions
    const inDemoMode = isInternalUser && demoSettings && (demoSettings === true || (typeof demoSettings === 'object' && demoSettings.active))

    return isDemoExternal || inDemoMode || false
  },
  getNetwork(inDemoMode, userRecord, organizations) {
    const getAssignedNetworks = () => {
      const { network: userNetworks } = userRecord || {}
      const isValidNetworkDefined = Array.isArray(userNetworks) && !userNetworks.find((network) => typeof network !== 'object')
      return (isValidNetworkDefined && userNetworks) || []
    }
    const allowedNetworks = inDemoMode ? this.makeDemoNetworks() : getAssignedNetworks()
    return allowedNetworks.map(Networks.mapNetworkToOrganization(organizations).bind(Networks))
  },

  modernizeLegacySavedFilters(network, reports) {
    const { defaults: legacyPreFilterProp } = network
    const isLegacySavedFilter = typeof legacyPreFilterProp === 'boolean'

    if (!network.reports) network.reports = reports || []
    else if (reports) network.reports.concat(reports)

    if (isLegacySavedFilter && !network.type) {
      network.type = legacyPreFilterProp ? 'prefilter' : 'postfilter'
    }

    return network
  },
  modernizeLegacyFilterSetting(filterSetting) {
    const { filter, parameters } = filterSetting
    if (!filter) filterSetting.filter = parameters || {}
    return filterSetting
  },
  /**
   * Determines whether the user has prefilters for the current selected search's report.
   * @return {array|boolean} An array of filter params, e.g. [{ operator: String, value: String }], or false
   */
  getCurrentSavedFilters(data = {}, limitToPreFilters) {
    const { filters, reportId, reportKey, subId } = data
    const invalidInput = !reportKey || !Array.isArray(filters)

    if (invalidInput) return null

    return filters.filter((filterSetting) => {
      const normalizedFilterSetting = user.modernizeLegacyFilterSetting(filterSetting)
      const { filter: filterBy, network: networks, reports: legacyReportsProp } = normalizedFilterSetting

      const findPreFilterParameters = () => {
        // Guard against undefined networks array
        if (!networks || !Array.isArray(networks)) {
          return false
        }

        const networkFilter = networks.find((network) => {
          const normalizedNetwork = user.modernizeLegacySavedFilters(network, legacyReportsProp)
          // @clean
          const { billingCode, organizationId, reports, teamId, type: filterType } = normalizedNetwork
          const isReportFilter = reports.includes(reportKey)
          const matchesReportId = () => [String(organizationId), billingCode].indexOf(String(reportId)) > -1
          const matchesTeamCondition = () => {
            const applyToAllTeams = String(teamId) === '-1'
            const matchesTeamId = String(teamId) === String(subId)
            return applyToAllTeams || matchesTeamId
          }
          const matchesPrefilterCondition = () => !limitToPreFilters || filterType === 'prefilter'

          return isReportFilter && matchesReportId() && matchesTeamCondition() && matchesPrefilterCondition()
        })

        return networkFilter ? filterBy : false
      }

      return findPreFilterParameters()
    })
  },

  mapUser(person) {
    const {
      access: assignedAccess,
      isActive,
      isAdmin,
      isInternal,
      network: assignedNetworks = [],
      settings: assignedSettings = {},
    } = person
    const {
      active = false,
      expires = null,
      level = isAdmin ? 'admin' : 'reader',
      permissions: assignedPermissions = {},
      role = isInternal ? 'internal' : 'external',
    } = assignedAccess || {}
    const {
      admin: permissionAdmin,
      alias: permissionAlias,
      bulk: permissionBulk,
      burtForecast: permissionBurtForecast,
      burtHub: permissionBurtHub,
      customReports: permissionCustomReports,
      demo: permissionDemo,
      dev: permissionDev,
      discrepancy: permissionDiscrepancy,
      enterprise: permissionEnterprise,
      exports: permissionExports,
      logs: permissionLogs,
      pacing: permissionPacing,
      programmatic: permissionProgrammatic,
      security: permissionSecurity,
      settings: permissionSettings,
      users: permissionUsers,
    } = assignedPermissions || {}
    const permissions = {
      ...assignedPermissions,
      admin: typeof permissionAdmin === 'boolean' && permissionAdmin,
      alias: typeof permissionAlias === 'boolean' && permissionAlias,
      bulk: typeof permissionBulk === 'boolean' && permissionBulk,
      burtForecast: typeof permissionBurtForecast === 'boolean' && permissionBurtForecast,
      burtHub: typeof permissionBurtHub === 'boolean' && permissionBurtHub,
      customReports: (Array.isArray(permissionCustomReports) && permissionCustomReports) || false,
      demo: typeof permissionDemo === 'boolean' && permissionDemo,
      dev: typeof permissionDev === 'boolean' && permissionDev,
      discrepancy: typeof permissionDiscrepancy === 'boolean' && permissionDiscrepancy,
      enterprise: typeof permissionEnterprise === 'boolean' && permissionEnterprise,
      exports: typeof permissionExports === 'boolean' && permissionExports,
      logs: typeof permissionLogs === 'boolean' && permissionLogs,
      pacing: typeof permissionPacing === 'boolean' && permissionPacing,
      programmatic: typeof permissionProgrammatic === 'boolean' && permissionProgrammatic,
      security: typeof permissionSecurity === 'boolean' && permissionSecurity,
      settings: permissionSettings !== false,
      users: typeof permissionUsers === 'boolean' && permissionUsers,
    }
    const { alias: definedAlias, filters: definedFilters = [] } = assignedSettings
    const hasValidAlias = typeof definedAlias === 'object'
    const hasValidUserFilters = typeof definedFilters === 'object' && Array.isArray(definedFilters) && definedFilters.length

    const settings = {
      ...assignedSettings,
      alias: (hasValidAlias && definedAlias) || {},
      bookmarks: assignedSettings.bookmarks || [],
      dashboard: assignedSettings.dashboard || {},
      demo: assignedSettings.demo || {},
      discrepancy: assignedSettings.discrepancy || {},
      filters: (hasValidUserFilters && definedFilters) || [],
      home: assignedSettings.home || {},
      messages: assignedSettings.messages || [],
      networks: assignedSettings.networks || [],
      pacing: assignedSettings.pacing || {},
      reports: assignedSettings.reports || [],
      themes: assignedSettings.themes || {},
      time: assignedSettings.time || {},
    }
    const access = {
      ...assignedAccess,
      active: [true, false].includes(active) ? active : isActive || false,
      expires,
      level,
      permissions,
      role,
    }
    const network = assignedNetworks.map((nw) => {
      const { organization, team: teams } = nw
      const { code: billingCode, id: publisherId, networkCode = null } = organization
      return {
        organization: {
          code: billingCode,
          id: publisherId,
          networkCode,
        },
        team: teams.map((team) => {
          const { code: teamCode, id: teamId } = team

          return {
            code: teamCode,
            id: teamId,
          }
        }),
      }
    })

    return {
      access,
      network,
      settings,
    }
  },
  async mapPersonAndUser(personUser, organizations) {
    const {
      access: assignedAccess,
      email = personUser.docId,
      isActive,
      isAdmin,
      isInternal,
      personId,
      personNameFirst,
      personNameLast,
      settings: assignedSettings = {},
    } = personUser
    const {
      active,
      expires = null,
      level = isAdmin ? 'admin' : 'reader',
      owner = null,
      permissions: assignedPermissions = {},
      role = isInternal ? 'internal' : 'external',
      type = 'user',
    } = assignedAccess || {}
    const {
      admin: permissionAdmin,
      alias: permissionAlias,
      bulk: permissionBulk,
      burtForecast: permissionBurtForecast,
      burtHub: permissionBurtHub,
      customReports: permissionCustomReports,
      demo: permissionDemo,
      dev: permissionDev,
      discrepancy: permissionDiscrepancy,
      enterprise: permissionEnterprise,
      exports: permissionExports,
      logs: permissionLogs,
      pacing: permissionPacing,
      programmatic: permissionProgrammatic,
      security: permissionSecurity,
      settings: permissionSettings,
      users: permissionUsers,
    } = assignedPermissions || {}

    const personErrors = async () => this.validatePerson(personUser)
    const userErrors = async () => this.validateUser(personUser)
    const authorizePersonErrors = this.authorizePerson(personUser)
    const authorizeUserErrors = this.authorizeUser(personUser)

    const isPersonAuthorized = authorizePersonErrors.length === 0
    const isUserAuthorized = authorizeUserErrors.length === 0
    const isUserActive = isActive && active

    const getCustomReports = () => Array.isArray(permissionCustomReports) && permissionCustomReports
    const permissions = {
      ...assignedPermissions,
      admin: typeof permissionAdmin === 'boolean' && permissionAdmin,
      alias: typeof permissionAlias === 'boolean' && permissionAlias,
      bulk: typeof permissionBulk === 'boolean' && permissionBulk,
      burtHub: typeof permissionBurtHub === 'boolean' && permissionBurtHub,
      burtForecast: typeof permissionBurtForecast === 'boolean' && permissionBurtForecast,
      customReports: getCustomReports() || !!permissionCustomReports,
      demo: typeof permissionDemo === 'boolean' && permissionDemo,
      dev: typeof permissionDev === 'boolean' && permissionDev,
      discrepancy: typeof permissionDiscrepancy === 'boolean' && permissionDiscrepancy,
      enterprise: typeof permissionEnterprise === 'boolean' && permissionEnterprise,
      exports: typeof permissionExports === 'boolean' && permissionExports,
      logs: typeof permissionLogs === 'boolean' && permissionLogs,
      pacing: typeof permissionPacing === 'boolean' && permissionPacing,
      programmatic: typeof permissionProgrammatic === 'boolean' && permissionProgrammatic,
      security: typeof permissionSecurity === 'boolean' && permissionSecurity,
      settings: typeof permissionSettings === 'boolean' && permissionSettings,
      users: typeof permissionUsers === 'boolean' && permissionUsers,
    }
    const access = {
      ...assignedAccess,
      active: isUserActive,
      expires: expires || '2200-01-01T00:00:00Z',
      level,
      owner,
      permissions,
      role,
      type,
    }

    const isUserInternal = personUser.isInternal && access?.role === 'internal'
    const isUserExternal = !isUserInternal
    const inDemoMode = this.inDemoMode(personUser)

    const assignedNetworks = this.getNetwork(inDemoMode, personUser, organizations)
    const networkIdsAndNames = {
      billingCodes: [],
      billingCodesExpired: [],
      customReportsActive: [],
      customReportsConfigured: [],
      customReportsPossible: [],
      exportPermissions: [],
      filters: [],
      networkCodes: [],
      networkTeamCodes: [],
      networkTeamIds: [],
      networkTeamNames: [],
      clientNames: [],
      teamCodes: [],
      teamIds: [],
      teamNames: [],
    }

    const hasExportsEnabledOnUser = [true, undefined].includes(permissions.exports)

    const hasCustomReports = permissions.customReports
    const userCustomReportsAll = hasCustomReports || []

    // @TODO Handle external user associated to org with hasAccessExpired.
    const networkIdentifiers = assignedNetworks.reduce((orgIdentifiers, network) => {
      const { organization = {}, publisher = {} } = network
      const { code, id, networkCode } = organization || {}

      orgIdentifiers.billingCodes.push(code)
      orgIdentifiers.networkCodes.push(String(networkCode))

      orgIdentifiers.clientNames.push(publisher.text)

      if (hasCustomReports) {
        const { customReports: orgCustomReports } = publisher?.access?.permissions || {}
        const userCustomReportHrefs = userCustomReportsAll.map((customReport) => customReport.href)
        const setCustomReports = () => {
          if (Array.isArray(orgCustomReports) && orgCustomReports.length) {
            const isPersonUserInternalAdmin = isUserInternal && level === 'admin'
            const customReportsConfiguredForAdmins = () => isPersonUserInternalAdmin && orgCustomReports
            const customReportsConfiguredForNonAdmins = () => orgCustomReports.filter((cr) => userCustomReportHrefs.indexOf(cr.href) !== -1)
            const customReportsConfigured = customReportsConfiguredForAdmins() || customReportsConfiguredForNonAdmins()
            const customReportsActive = customReportsConfigured.filter((cr) => cr.active)

            orgIdentifiers.customReportsPossible.push(...orgCustomReports)
            orgIdentifiers.customReportsActive.push(...customReportsActive)
            orgIdentifiers.customReportsConfigured.push(...customReportsConfigured)
          }
        }

        if (Array.isArray(orgCustomReports)) setCustomReports()
      }

      // @TODO Ask Henry if there are any API props we should ref to manage access at org level.
      if (isUserExternal && network.publisher) {
        if (network.publisher.hasAccessExpired) {
          orgIdentifiers.billingCodesExpired.push(network.organization?.code)
        }

        const hasExportEnabledOnCurrentOrg = network.publisher.hasExportsEnabled

        if (typeof hasExportEnabledOnCurrentOrg === 'boolean') {
          orgIdentifiers.exportPermissions.push(network.publisher.hasExportsEnabled)
        } else {
          orgIdentifiers.exportPermissions.push(true)
        }
      }

      if (Array.isArray(network.team)) {
        network.team.forEach((team) => {
          const { code: teamCode, id: teamId } = team
          const teamIdString = String(teamId)

          orgIdentifiers.networkTeamCodes.push(`${code}/${teamCode}`)
          orgIdentifiers.networkTeamIds.push(`${id}/${teamId}`)

          const currentTeam = publisher?.teams?.find((t) => String(t.teamId) === teamIdString && t.teamCode === teamCode)

          orgIdentifiers.networkTeamNames.push(currentTeam?.teamName || 'Team Name Unknown')
        })
      }

      return orgIdentifiers
    }, networkIdsAndNames)

    const hasCustomReportsActive = !!networkIdentifiers.customReportsActive.length
    const hasOrgCustomReportErrors = async () => {
      const validateExternal = () => (userCustomReportsAll.length && !networkIdentifiers.customReportsConfigured.length && userCustomReportsAll.map((customReport, index) => [`organization/custom-reports/not-found:${customReport.href || index}`])) || networkIdentifiers.customReportsConfigured.filter((customReport) => !customReport.href || typeof customReport.active !== 'boolean').map((orgCustomReportConfig, index) => [`organization/custom-reports/misconfig/${orgCustomReportConfig.href || index}`])
      return isUserInternal || validateExternal()
    }
    const hasUserCustomReportErrors = async () => {
      const validateExternal = () => userCustomReportsAll.reduce((foundCustomReportErrors, customReportConfiig, index) => {
        const foundOrgConfig = networkIdentifiers.customReportsConfigured.find((customReport) => customReport.href === customReportConfiig.href)
        if (typeof foundOrgConfig !== 'object') {
          foundCustomReportErrors.push(`user/custom-reports/not-found:${customReportConfiig.href || index}`)
        } else if (typeof foundOrgConfig.active !== 'boolean') {
          foundCustomReportErrors.push(`user/custom-reports/misconfig:${customReportConfiig.href || index}`)
        }
        return foundCustomReportErrors
      }, [])
      const validateInternal = () => isUserInternal && userCustomReportsAll.filter((customReport) => typeof customReport.href !== 'string' || typeof customReport.active !== 'boolean').map((customReport, index) => [`user/custom-reports/misconfig:${customReport.href || index}`])

      return hasCustomReports && (validateInternal() || validateExternal())
    }

    const hasExportsEnabledOnAllOrgs = networkIdentifiers.exportPermissions.filter((permission) => permission).length === networkIdentifiers.exportPermissions.length
    const hasExportsEnabled = hasExportsEnabledOnUser && hasExportsEnabledOnAllOrgs

    const hasTeamsLimited = !isUserInternal && !!assignedNetworks.find((network) => {
      const { team } = network
      return Array.isArray(team) && team.find((t) => !t || typeof t !== 'object' || t.id?.toString() !== '-1')
    })

    const settings = this.normalizeSettings(assignedSettings)

    // Internal Team Assignment
    const isUserTeamExecutive = personUser.teamId === 14
    const isUserTeamBts = personUser.teamId === 9
    const isUserTeamSales = personUser.teamId === 11

    // Access Role Checks
    const canAdmin = level === 'admin'
    const canWrite = ['admin', 'writer'].indexOf(level) > -1
    const canRead = ['admin', 'reader', 'writer'].indexOf(level) > -1

    const isAccessLevelAdmin = isAdmin && canAdmin
    const isAccessLevelWriter = level === 'writer'

    const isUserInternalAdmin = isUserInternal && isAccessLevelAdmin
    const isUserExternalAdmin = isUserExternal && isAccessLevelAdmin

    // Custom Roles
    const hasCustomRoleAdmin = isAccessLevelAdmin && permissions.admin === true
    const hasCustomRoleDev = isAccessLevelAdmin && permissions.dev === true

    // Custom Role Access Grants
    const hasAccessToEnterpriseProfile = hasCustomRoleAdmin && permissions.enterprise === true
    const hasAccessToLogs = hasCustomRoleAdmin && permissions.logs === true
    const hasAccessToSecurityCenter = hasCustomRoleAdmin && permissions.security === true
    const hasAccessToUserManagement = hasCustomRoleAdmin && permissions.users === true
    const hasAccessToProjectManagement = hasCustomRoleAdmin && permissions.project === 'admin' && isUserInternal

    // The Settings Module is always accessible unless explicitly disabled
    const hasAccessToSettingsModule = permissions.settings !== false

    // Settings
    const { alert = {}, alias = {}, filters } = settings

    const inAliasMode = !!alias && typeof alias === 'object' && alias.active

    const hasAlerts = !alert?.dismissed
    const hasFilters = !!filters?.length

    const nameFirst = personNameFirst?.trim()
    const nameLast = personNameLast?.trim()

    const { billingCodes, billingCodesExpired } = networkIdentifiers
    const hasActiveOrgs = billingCodes.find((billingCode) => !billingCodesExpired.includes(billingCode)) || false

    // Validation
    const accessErrorPromises = [personErrors(), userErrors(), hasUserCustomReportErrors(), hasOrgCustomReportErrors()]
    const accessErrorSets = await Promise.all(accessErrorPromises)
    const hasAccessErrors = accessErrorSets.reduce((allErrors, foundErrors) => {
      if (foundErrors && foundErrors.length) allErrors.push(...foundErrors)
      return allErrors
    }, [])

    const authErrorSets = [authorizePersonErrors, authorizeUserErrors]
    const hasAuthErrors = authErrorSets.reduce((allErrors, foundErrors) => {
      if (foundErrors && foundErrors.length) allErrors.push(...foundErrors)
      return allErrors
    }, [])

    const isConfigValid = hasAccessErrors.length === 0

    /**
     * Determine the user's permissions based on the level, role, and
     * access permissions associated with the user and their organizations.
     */

    // Determine whether the user can access iAdOps based on their
    // configurations and associated organizations.
    const canAccessApps = isConfigValid // passes validation
      && isPersonAuthorized // is active & not deleted
      && isUserAuthorized // is active & not expired
      && (isUserInternal || hasActiveOrgs?.length > 0) // external is related to active org

    return {
      ...personUser,
      access,
      email,

      // Person and User Access Grants
      'access.active': access.active,
      'access.expires': access.expires,
      'access.level': access.level,
      'access.role': access.role,

      'access.permissions.admin': permissions.admin,
      'access.permissions.alias': permissions.alias,
      'access.permissions.bulk': permissions.bulk,
      'access.permissions.burtForecast': permissions.burtForecast,
      'access.permissions.burtHub': permissions.burtHub,
      'access.permissions.customReports': permissions.customReports,
      'access.permissions.demo': permissions.demo,
      'access.permissions.dev': permissions.dev,
      'access.permissions.discrepancy': permissions.discrepancy,
      'access.permissions.enterprise': permissions.enterprise,
      'access.permissions.exports': permissions.exports,
      'access.permissions.logs': permissions.logs,
      'access.permissions.pacing': permissions.pacing,
      'access.permissions.programmatic': permissions.programmatic,
      'access.permissions.project': permissions.project,
      'access.permissions.security': permissions.security,
      'access.permissions.settings': permissions.settings,
      'access.permissions.users': permissions.users,

      canAccessApps,

      // Access Level Checks
      canAdmin,
      canRead,
      canWrite,

      isAccessLevelAdmin,
      isAccessLevelWriter,

      // Custom Roles
      hasCustomRoleAdmin,
      hasCustomRoleDev,

      // Custom Role Access Grants
      hasAccessToEnterpriseProfile,
      hasAccessToLogs,
      hasAccessToProjectManagement,
      hasAccessToSecurityCenter,
      hasAccessToSettingsModule,
      hasAccessToUserManagement,

      // Validation Errors
      hasAccessErrors,
      hasAuthErrors,

      hasActiveOrgs,

      // Feature Permissions
      hasAlerts,
      hasCustomReports,
      hasCustomReportsActive,
      hasExportsEnabled,
      hasFilters,
      hasTeamsLimited,

      // Enabled/Disabled States
      inAliasMode,
      inDemoMode,

      // Validation
      isConfigValid,

      // Person Properties
      isPersonAuthorized,
      // isPersonInternal: See isInternal, inherited from Person.

      // User Properties
      isUserAuthorized,
      isUserActive,
      isUserInternal,

      isUserExternalAdmin,
      isUserInternalAdmin,

      isUserTeamBts,
      isUserTeamExecutive,
      isUserTeamSales,

      // Assignments
      network: assignedNetworks,
      ...networkIdentifiers,

      // Identifiers
      personId,
      personName: `${nameFirst} ${nameLast}`,
      personNameFirst: nameFirst,
      personNameLast: nameLast,
      personRole: role || (personUser?.isInternal && 'internal') || 'external',

      settings,
    }
  },

  aliasId(userRecord) {
    const hasPermissions = userRecord && userRecord.access && (userRecord.access.role === 'internal' || userRecord.access.permissions?.alias)
    const aliasSettings = userRecord?.settings?.alias
    const isAliasActive = aliasSettings?.active

    return hasPermissions && isAliasActive && aliasSettings.email
  },
}

export default user
